/**
 * boothCameraProfiles.js ✦ iter198
 * ─────────────────────────────────────────────────────────────
 * The single source of truth for capture configuration across
 * every camera surface in the platform.
 *
 * Per Craig's iter198 directive: "guests subconsciously interpret
 * zoom mismatch, framing drift, mirror mismatch, aspect ratio
 * jumps, jerky transitions, encoding differences as cheapness —
 * even if they never articulate it." A pipeline is only a
 * pipeline if every surface that uses it consumes the SAME
 * profile vocabulary. This file is that vocabulary.
 *
 * Each profile centrally defines:
 *
 *   • aspectRatio        — preferred composition ratio
 *   • resolution         — { width, height } ideal hints
 *   • facingMode         — initial camera (defaults to 'user')
 *   • frameRate          — { ideal, max } target hints
 *   • audio              — true | false (still vs video flows)
 *   • mirrorMode         — 'mirror-preview-only' | 'mirror-export' | 'no-mirror'
 *   • exportMime         — preferred still-frame export type
 *   • exportQuality      — 0..1 JPEG/WebP quality
 *   • videoMimeCandidates— ordered MediaRecorder negotiation list
 *   • videoBitsPerSecond — target bitrate when supported
 *   • cropAnchor         — 'center' | 'top' | 'bottom' (frame the subject)
 *   • safeZone           — { x, y, w, h } normalized 0..1 (no critical
 *                          content outside this box → guards against
 *                          ultrawide / Safari front-cam scaling drift)
 *
 * Mirror semantics (Craig, iter197 close-out):
 *
 *   "Exported memories should reflect the physical room, not the
 *    camera preview illusion. Booth OS, Memory Snap, Pocket Booth,
 *    Spin Film, Video Guestbook → mirror-preview-only. Lens Notes
 *    is the exception — performative handwriting, not documentation
 *    — so it stays mirror-export end-to-end."
 */

export const MIRROR = Object.freeze({
  PREVIEW_ONLY: 'mirror-preview-only',
  EXPORT: 'mirror-export',
  NONE: 'no-mirror',
});

const baseStill = {
  audio: false,
  exportMime: 'image/jpeg',
  exportQuality: 0.92,
  videoMimeCandidates: [],
  videoBitsPerSecond: 0,
  cropAnchor: 'center',
  // ── iter199 ✦ composition language (host-facing framing
  //    calibration; never visible to guests by default) ────────
  compositionFrame: 'face',
  headroomGuide: 0.40,
  subjectAnchor: 'eyes',
  captureDistance: 'close',
};

const baseVideo = {
  audio: true,
  exportMime: 'image/jpeg',
  exportQuality: 0.9,
  videoMimeCandidates: [
    // Order matters — ranked by playback compatibility across
    // venue download paths (parents downloading from the gallery
    // need .mp4 fallback on iOS; chrome desktop is happiest with
    // VP9). The pipeline negotiates this list at start().
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
  ],
  videoBitsPerSecond: 2_500_000,
  cropAnchor: 'center',
  // iter199 — composition language defaults for video flows.
  compositionFrame: 'portrait',
  headroomGuide: 0.42,
  subjectAnchor: 'shoulders',
  captureDistance: 'medium',
};

export const BOOTH_CAMERA_PROFILES = Object.freeze({
  // ── Booth OS Memory Snap & general kiosk stills ─────────────
  booth: Object.freeze({
    ...baseStill,
    aspectRatio: 4 / 3,
    resolution: { width: 1280, height: 960 },
    facingMode: 'user',
    frameRate: { ideal: 30, max: 30 },
    mirrorMode: MIRROR.PREVIEW_ONLY,
    safeZone: { x: 0.06, y: 0.08, w: 0.88, h: 0.84 },
    compositionFrame: 'face',
    headroomGuide: 0.38,
    subjectAnchor: 'eyes',
    captureDistance: 'close',
  }),

  // ── Personal-device portrait stills (Memory Snap on phone,
  //    Pocket Booth selfie). Slightly tighter safe zone because
  //    Safari front-cam scaling drifts inwards. ─────────────────
  portrait: Object.freeze({
    ...baseStill,
    aspectRatio: 3 / 4,
    resolution: { width: 960, height: 1280 },
    facingMode: 'user',
    frameRate: { ideal: 30, max: 30 },
    mirrorMode: MIRROR.PREVIEW_ONLY,
    safeZone: { x: 0.08, y: 0.10, w: 0.84, h: 0.80 },
    compositionFrame: 'face',
    headroomGuide: 0.40,
    subjectAnchor: 'eyes',
    captureDistance: 'close',
  }),

  // ── Lens Notes ─ performative handwriting. Mirrored end-to-end. ─
  lens: Object.freeze({
    ...baseStill,
    // iter211d ✦ — Lens Notes records a VIDEO with handwriting +
    // narration. baseStill defaults audio off; we override here so
    // getUserMedia includes the mic track. The LensNotePage merges
    // it onto the composite canvas stream before MediaRecorder
    // takes over — without this override, every Lens Note was a
    // silent MP4 on production.
    audio: true,
    aspectRatio: 4 / 3,
    resolution: { width: 1280, height: 960 },
    facingMode: 'user',
    frameRate: { ideal: 30, max: 30 },
    mirrorMode: MIRROR.EXPORT,
    safeZone: { x: 0.05, y: 0.05, w: 0.90, h: 0.90 },
    compositionFrame: 'handwriting',
    headroomGuide: 0.50,
    subjectAnchor: 'pen-tip',
    captureDistance: 'intimate',
  }),

  // ── Spin Film 360 ─ shared acquisition only. The cinematic
  //    sweep choreography stays in the consumer (Booth360Page).
  //    Mirror disabled because the spin lands on the rear-facing
  //    camera on most kiosks. ────────────────────────────────────
  spin360: Object.freeze({
    ...baseStill,
    aspectRatio: 16 / 9,
    resolution: { width: 1920, height: 1080 },
    facingMode: 'environment',
    frameRate: { ideal: 60, max: 60 },
    mirrorMode: MIRROR.NONE,
    safeZone: { x: 0.10, y: 0.12, w: 0.80, h: 0.76 },
    audio: false,
    compositionFrame: 'wide',
    headroomGuide: 0.50,
    subjectAnchor: 'center',
    captureDistance: 'wide',
  }),

  // ── Video Guestbook / Audio Guestbook (STANDALONE) ────────────
  //    Used by `GuestbookRecorder.js` only. Audio is acquired
  //    separately via `useParticipationAudioPipeline` (profile
  //    `voice_message`) and composed onto the camera's video
  //    track at the consumer:
  //      new MediaStream([videoTrack, processedAudioTrack])
  //    so the audio-only and video-mode guestbooks share an
  //    identical processed audio signature. (Craig, iter206.)
  //    For the booth-shell consumer (BoothVideoMessage) which does
  //    NOT layer the audio pipeline in, use `guestbook_booth`. ─
  guestbook: Object.freeze({
    ...baseVideo,
    audio: false,
    aspectRatio: 4 / 3,
    resolution: { width: 1280, height: 960 },
    facingMode: 'user',
    frameRate: { ideal: 30, max: 30 },
    mirrorMode: MIRROR.PREVIEW_ONLY,
    safeZone: { x: 0.08, y: 0.10, w: 0.84, h: 0.80 },
    compositionFrame: 'portrait',
    headroomGuide: 0.42,
    subjectAnchor: 'shoulders',
    captureDistance: 'medium',
  }),

  // ── Photo Strip ─ three-shot ceremony, same constraints as
  //    booth stills but locked at the booth profile for parity. ─
  strip: Object.freeze({
    ...baseStill,
    aspectRatio: 4 / 3,
    resolution: { width: 1280, height: 960 },
    facingMode: 'user',
    frameRate: { ideal: 30, max: 30 },
    mirrorMode: MIRROR.PREVIEW_ONLY,
    safeZone: { x: 0.06, y: 0.08, w: 0.88, h: 0.84 },
    compositionFrame: 'face',
    headroomGuide: 0.40,
    subjectAnchor: 'eyes',
    captureDistance: 'close',
  }),

  // ── Booth Video Message (BOOTH-OS SHELL) ──────────────────────
  //    iter211d ✦ — the standalone guestbook layers
  //    useParticipationAudioPipeline on top of the camera stream.
  //    The booth-os shell consumer (`BoothVideoMessage.jsx`) does
  //    NOT do that — it calls `cam.startRecording()` against the
  //    camera stream directly. So this profile MUST request audio
  //    in getUserMedia, otherwise every booth video message was
  //    a silent MP4 (the production fracture). Same framing as
  //    `guestbook`; the only delta is `audio: true`. ─────────────
  guestbook_booth: Object.freeze({
    ...baseVideo,
    audio: true,
    aspectRatio: 4 / 3,
    resolution: { width: 1280, height: 960 },
    facingMode: 'user',
    frameRate: { ideal: 30, max: 30 },
    mirrorMode: MIRROR.PREVIEW_ONLY,
    safeZone: { x: 0.08, y: 0.10, w: 0.84, h: 0.80 },
    compositionFrame: 'portrait',
    headroomGuide: 0.42,
    subjectAnchor: 'shoulders',
    captureDistance: 'medium',
  }),
});

/**
 * Resolve a profile by name. Throws (loudly) on unknown profile so
 * a typo at the call site never silently picks 'booth' — the whole
 * point of the pipeline is that profile drift is detectable.
 */
export function getCameraProfile(name) {
  const p = BOOTH_CAMERA_PROFILES[name];
  if (!p) {
    const known = Object.keys(BOOTH_CAMERA_PROFILES).join(', ');
    throw new Error(
      `[useBoothCameraPipeline] unknown camera profile "${name}". `
      + `Known profiles: ${known}.`,
    );
  }
  return p;
}

/**
 * Translate a profile to a getUserMedia constraints payload. Kept
 * pure so tests can assert the exact constraints any surface will
 * end up requesting from the browser.
 */
export function profileToConstraints(profile, override = {}) {
  const facingMode = override.facingMode || profile.facingMode;
  return {
    audio: profile.audio,
    video: {
      facingMode,
      width: { ideal: profile.resolution.width },
      height: { ideal: profile.resolution.height },
      aspectRatio: { ideal: profile.aspectRatio },
      frameRate: {
        ideal: profile.frameRate.ideal,
        max: profile.frameRate.max,
      },
    },
  };
}

/**
 * Whether the live preview should be mirrored horizontally. Driven
 * purely by mirrorMode + which camera is active. The rear-facing
 * camera is NEVER mirrored — that always feels broken.
 */
export function shouldMirrorPreview(profile, facingMode) {
  if (facingMode !== 'user') return false;
  return profile.mirrorMode !== MIRROR.NONE;
}

/**
 * Whether the EXPORTED frame should be mirrored. Independent of
 * preview mirroring — that's the whole point of the per-surface
 * override the user asked for.
 */
export function shouldMirrorExport(profile, facingMode) {
  if (facingMode !== 'user') return false;
  return profile.mirrorMode === MIRROR.EXPORT;
}
